<template>
  <el-card shadow="never" class="page-card">
    <div slot="header" class="card-header">
      <div class="card-title">
        <span class="page-icon">📅</span>
        <span>每日一关</span>
      </div>
      <div class="header-actions">
        <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="loadList">刷新表格</el-button>
        <el-button
          size="small"
          type="primary"
          icon="el-icon-view"
          :loading="tilesDialogLoading"
          @click="openDailyTilesDialog"
        >
          预览今日
        </el-button>
      </div>
    </div>

    <p class="sort-hint">
      <i class="el-icon-info-circle hint-icon" />
      当前页内拖左侧 <i class="el-icon-rank" /> 可排序；行内<strong>上一个 / 下一个</strong>按<strong>全局顺序</strong>与相邻条交换。
    </p>

    <el-form inline class="upload-bar">
      <el-form-item label="难度">
        <el-select v-model="difficulty" placeholder="难度" style="width: 130px">
          <el-option label="低 (3×4)" value="low" />
          <el-option label="中 (4×6)" value="medium" />
          <el-option label="高 (5×7)" value="high" />
        </el-select>
      </el-form-item>
      <el-form-item label="图片">
        <div class="file-input-wrapper">
          <input
            ref="fileInput"
            type="file"
            multiple
            accept="image/*"
            class="file-input"
            @change="onFileChange"
          />
          <span v-if="pendingFiles.length" class="file-hint">
            <i class="el-icon-picture-outline" /> 已选 {{ pendingFiles.length }} 个文件
          </span>
        </div>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" :loading="uploading" :disabled="!pendingFiles.length" @click="doUpload">
          <i class="el-icon-upload2" /> 批量上传
        </el-button>
      </el-form-item>
    </el-form>

    <el-table
      ref="table"
      v-loading="loading || sliceSaving"
      row-key="id"
      highlight-current-row
      :data="list"
      stripe
      border
      style="width: 100%; margin-top: 16px"
      @current-change="onCurrentRow"
    >
      <el-table-column label="" width="48" align="center" class-name="col-drag">
        <template slot-scope="{}">
          <i class="el-icon-rank row-drag-handle" title="拖动排序（仅本页）" @click.stop />
        </template>
      </el-table-column>
      <el-table-column label="缩略图" width="96" align="center">
        <template slot-scope="{ row }">
          <div class="thumb-cell">
            <el-image
              v-if="row.thumbSrc"
              class="thumb-img"
              :src="row.thumbSrc"
              fit="cover"
              :preview-src-list="[row.thumbSrc]"
            >
              <div slot="error" class="thumb-fallback">失败</div>
            </el-image>
            <div v-else-if="row.thumbLoading" class="thumb-fallback">加载中</div>
            <div v-else class="thumb-fallback" @click="loadRowThumb(row)">点加载</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="轮廓" width="96" align="center">
        <template slot-scope="{ row }">
          <div class="thumb-cell">
            <el-image
              v-if="row.silhouetteSrc"
              class="thumb-img thumb-img--silhouette"
              :src="row.silhouetteSrc"
              fit="contain"
              :preview-src-list="[row.silhouetteSrc]"
            >
              <div slot="error" class="thumb-fallback">失败</div>
            </el-image>
            <div v-else-if="row.thumbLoading" class="thumb-fallback">加载中</div>
            <div v-else-if="row.silhouetteMissing" class="thumb-fallback thumb-fallback--muted">无</div>
            <div v-else class="thumb-fallback" @click="loadRowThumb(row)">点加载</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="num" label="序号" width="72" align="center" />
      <el-table-column prop="id" label="ID" min-width="200" show-overflow-tooltip />
      <el-table-column label="难度" width="88" align="center">
        <template slot-scope="{ row }">
          <el-tag size="small" :type="difficultyTagType(row.difficulty)">{{ difficultyLabel(row.difficulty) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="网格" width="100" align="center">
        <template slot-scope="{ row }">{{ row.cols }} × {{ row.rows }}</template>
      </el-table-column>
      <el-table-column prop="tile_count" label="块数" width="72" align="center" />
      <el-table-column prop="display_date" label="展示日" width="120" show-overflow-tooltip />
      <el-table-column prop="created_at" label="上传时间" min-width="150" show-overflow-tooltip />
	  <el-table-column label="猜猜我是谁" width="168" align="center">
	    <template slot-scope="{ row }">
	      <div class="guess-cell">
	        <el-tag size="mini" :type="row.guess_configured ? 'success' : 'info'">
	          {{ row.guess_configured ? '已配置' : '未配置' }}
	        </el-tag>
	        <el-button type="text" size="mini" @click.stop="openGuessDialog(row)">编辑 ABCD</el-button>
	      </div>
	    </template>
	  </el-table-column>
      <el-table-column label="排序" width="148" align="center">
        <template slot-scope="{ row }">
          <el-button
            type="text"
            size="mini"
            icon="el-icon-top"
            :disabled="!rowCanMovePrev(row) || swappingRowId === row.id || sliceSaving"
            @click.stop="rowMovePrev(row)"
          >
            上一个
          </el-button>
          <el-button
            type="text"
            size="mini"
            icon="el-icon-bottom"
            :disabled="!rowCanMoveNext(row) || swappingRowId === row.id || sliceSaving"
            @click.stop="rowMoveNext(row)"
          >
            下一个
          </el-button>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="88" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="danger" size="mini" plain :loading="deletingId === row.id" @click="onDelete(row)">
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <el-pagination
        background
        layout="total, prev, pager, next, sizes"
        :page-sizes="[10, 20, 50]"
        :current-page.sync="page"
        :page-size.sync="pageSize"
        :total="total"
        @current-change="loadList"
        @size-change="onSizeChange"
      />
    </div>

    <el-dialog
      title="GET /api/tiles 返回数据"
      :visible.sync="tilesDialogVisible"
      width="800px"
      append-to-body
      @closed="onTilesDialogClosed"
    >
      <div v-loading="tilesDialogLoading" class="tiles-dialog-body">
        <template v-if="tilesPayload && tilesPayload.success">
          <p class="tiles-meta">
            success: {{ tilesPayload.success }} · count: {{ tilesPayload.count }} · config:
            {{ (tilesPayload.config && tilesPayload.config.cols) || '—' }} ×
            {{ (tilesPayload.config && tilesPayload.config.rows) || '—' }}
          </p>
          <div class="tiles-grid">
            <el-image
              v-for="(src, i) in tilesPayload.tiles"
              :key="i"
              class="tile-cell"
              :src="src"
              fit="cover"
              :preview-src-list="tilesPayload.tiles"
            />
          </div>
        </template>
        <el-alert v-else-if="tilesError" :title="tilesError" type="error" :closable="false" show-icon />
        <el-empty v-else-if="!tilesDialogLoading" description="无数据" />
      </div>
    </el-dialog>

    <el-dialog
      title="猜猜我是谁 · 四选项（入库）"
      :visible.sync="guessDialogVisible"
      width="520px"
      append-to-body
      @closed="onGuessDialogClosed"
    >
      <el-form label-width="88px" size="small" class="guess-form">
        <el-form-item label="选项 A">
          <el-input v-model="guessForm.option_a" maxlength="255" show-word-limit placeholder="必填" />
        </el-form-item>
        <el-form-item label="选项 B">
          <el-input v-model="guessForm.option_b" maxlength="255" show-word-limit placeholder="必填" />
        </el-form-item>
        <el-form-item label="选项 C">
          <el-input v-model="guessForm.option_c" maxlength="255" show-word-limit placeholder="必填" />
        </el-form-item>
        <el-form-item label="选项 D">
          <el-input v-model="guessForm.option_d" maxlength="255" show-word-limit placeholder="必填" />
        </el-form-item>
        <el-form-item label="正确答案">
          <el-radio-group v-model="guessForm.correct">
            <el-radio label="a">A</el-radio>
            <el-radio label="b">B</el-radio>
            <el-radio label="c">C</el-radio>
            <el-radio label="d">D</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="guessDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="guessSaving" @click="submitGuessOptions">保存</el-button>
      </div>
    </el-dialog>
  </el-card>
</template>

<script>
import Sortable from 'sortablejs'
import {
  fetchDailyTiles,
  fetchDailyPuzzles,
  batchUploadDailyPuzzles,
  deleteDailyPuzzle,
  fetchDailyPuzzlePreview,
  reorderDailySlice,
  swapDailyPuzzle,
  saveDailyPuzzleGuessOptions
} from '@/api/dailyPuzzle'
import { Message, MessageBox } from 'element-ui'

export default {
  name: 'DailyPuzzles',
  data() {
    return {
      difficulty: 'medium',
      pendingFiles: [],
      uploading: false,
      list: [],
      loading: false,
      total: 0,
      page: 1,
      pageSize: 20,
      deletingId: '',
      currentRow: null,
      swappingRowId: null,
      sliceSaving: false,
      _rowSortable: null,
      tilesDialogVisible: false,
      tilesDialogLoading: false,
      tilesPayload: null,
      tilesError: '',
      guessDialogVisible: false,
      guessSaving: false,
      guessTargetId: null,
      guessForm: {
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        correct: 'a'
      }
    }
  },
  computed: {
    lastPage() {
      if (!this.total) return 1
      return Math.max(1, Math.ceil(this.total / this.pageSize))
    }
  },
  created() {
    this.loadList()
  },
  beforeDestroy() {
    this.destroyRowSortable()
  },
  methods: {
    difficultyLabel(d) {
      const m = { low: '低', medium: '中', high: '高' }
      return m[d] || d || '-'
    },
    difficultyTagType(d) {
      const m = { low: 'success', medium: 'warning', high: 'danger' }
      return m[d] || 'info'
    },
    onCurrentRow(row) {
      this.currentRow = row
    },
    rowCanMovePrev(row) {
      const idx = this.list.findIndex((r) => r.id === row.id)
      if (idx < 0) return false
      return !(this.page === 1 && idx === 0)
    },
    rowCanMoveNext(row) {
      const idx = this.list.findIndex((r) => r.id === row.id)
      if (idx < 0) return false
      return !(this.page === this.lastPage && idx === this.list.length - 1)
    },
    onFileChange(e) {
      const files = e.target.files
      this.pendingFiles = files ? Array.from(files) : []
    },
    onSizeChange() {
      this.page = 1
      this.loadList()
    },
    destroyRowSortable() {
      if (this._rowSortable) {
        this._rowSortable.destroy()
        this._rowSortable = null
      }
    },
    bindRowSortable() {
      this.destroyRowSortable()
      const table = this.$refs.table
      const tbody = table && table.$el && table.$el.querySelector('.el-table__body-wrapper tbody')
      if (!tbody || !this.list.length) return

      this._rowSortable = Sortable.create(tbody, {
        handle: '.row-drag-handle',
        animation: 180,
        onEnd: (evt) => {
          const { oldIndex, newIndex } = evt
          if (oldIndex === newIndex || oldIndex == null || newIndex == null) return
          this.destroyRowSortable()
          const next = [...this.list]
          const [moved] = next.splice(oldIndex, 1)
          next.splice(newIndex, 0, moved)
          this.list = next
          this.persistSliceOrder()
        }
      })
    },
    async persistSliceOrder() {
      const offset = (this.page - 1) * this.pageSize
      const orderedIds = this.list.map((r) => r.id)
      this.sliceSaving = true
      try {
        const res = await reorderDailySlice(offset, orderedIds)
        if (res.success) {
          Message.success('本页顺序已更新')
        } else {
          Message.error(res.error || '保存失败')
        }
      } catch (e) {
        const d = e.response && e.response.data
        Message.error((d && d.error) || e.message || '保存失败')
      } finally {
        this.sliceSaving = false
        await this.loadList()
      }
    },
    async rowMovePrev(row) {
      const id = row.id
      if (!this.rowCanMovePrev(row)) return
      this.swappingRowId = id
      try {
        const res = await swapDailyPuzzle(id, 'prev')
        if (res.success) {
          Message.success('已与上一条交换')
        } else {
          Message.error(res.error || '操作失败')
        }
      } catch (e) {
        const d = e.response && e.response.data
        Message.error((d && d.error) || e.message || '操作失败')
      } finally {
        this.swappingRowId = null
        await this.loadList()
      }
    },
    async rowMoveNext(row) {
      const id = row.id
      if (!this.rowCanMoveNext(row)) return
      this.swappingRowId = id
      try {
        const res = await swapDailyPuzzle(id, 'next')
        if (res.success) {
          Message.success('已与下一条交换')
        } else {
          Message.error(res.error || '操作失败')
        }
      } catch (e) {
        const d = e.response && e.response.data
        Message.error((d && d.error) || e.message || '操作失败')
      } finally {
        this.swappingRowId = null
        await this.loadList()
      }
    },
    async loadList() {
      this.destroyRowSortable()
      this.loading = true
      try {
        const res = await fetchDailyPuzzles({
          page: this.page,
          page_size: this.pageSize
        })
        if (res.success) {
          this.list = (res.items || []).map((r) => ({
            ...r,
            thumbSrc: '',
            silhouetteSrc: '',
            silhouetteMissing: false,
            thumbLoading: false
          }))
          this.total = res.total || 0
        } else {
          this.list = []
          Message.error(res.error || '加载失败')
        }
      } catch (e) {
        this.list = []
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.bindRowSortable()
          this.hydrateThumbs()
        })
      }
    },
    async hydrateThumbs() {
      await Promise.all(
        this.list.map(async (row) => {
          this.$set(row, 'thumbLoading', true)
          try {
            const res = await fetchDailyPuzzlePreview(row.id)
            if (res.success && res.original) {
              this.$set(row, 'thumbSrc', res.original)
            }
            if (res.success && res.silhouette) {
              this.$set(row, 'silhouetteSrc', res.silhouette)
              this.$set(row, 'silhouetteMissing', false)
            } else if (res.success) {
              this.$set(row, 'silhouetteSrc', '')
              this.$set(row, 'silhouetteMissing', true)
            }
          } catch (e) {
            /* 忽略单条缩略图失败 */
          } finally {
            this.$set(row, 'thumbLoading', false)
          }
        })
      )
    },
    async loadRowThumb(row) {
      if (row.thumbLoading) return
      if (row.thumbSrc && (row.silhouetteSrc || row.silhouetteMissing)) return
      this.$set(row, 'thumbLoading', true)
      try {
        const res = await fetchDailyPuzzlePreview(row.id)
        if (res.success && res.original) {
          this.$set(row, 'thumbSrc', res.original)
        }
        if (res.success && res.silhouette) {
          this.$set(row, 'silhouetteSrc', res.silhouette)
          this.$set(row, 'silhouetteMissing', false)
        } else if (res.success) {
          this.$set(row, 'silhouetteSrc', '')
          this.$set(row, 'silhouetteMissing', true)
        }
      } finally {
        this.$set(row, 'thumbLoading', false)
      }
    },
    async openDailyTilesDialog() {
      this.tilesDialogVisible = true
      this.tilesDialogLoading = true
      this.tilesPayload = null
      this.tilesError = ''
      try {
        const res = await fetchDailyTiles()
        if (res && res.success) {
          this.tilesPayload = res
        } else {
          this.tilesError = (res && res.error) || '请求失败'
        }
      } catch (e) {
        const d = e.response && e.response.data
        this.tilesError = (d && d.error) || e.message || '网络错误'
      } finally {
        this.tilesDialogLoading = false
      }
    },
    onTilesDialogClosed() {
      this.tilesPayload = null
      this.tilesError = ''
      this.loadList()
    },
    onGuessDialogClosed() {
      this.guessTargetId = null
      this.guessForm = {
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        correct: 'a'
      }
    },
    openGuessDialog(row) {
      this.guessTargetId = row.id
      this.guessForm = {
        option_a: row.guess_option_a || '',
        option_b: row.guess_option_b || '',
        option_c: row.guess_option_c || '',
        option_d: row.guess_option_d || '',
        correct: ['a', 'b', 'c', 'd'].includes(row.guess_correct) ? row.guess_correct : 'a'
      }
      this.guessDialogVisible = true
    },
    async submitGuessOptions() {
      const id = this.guessTargetId
      if (!id) return
      const f = this.guessForm
      const keys = ['option_a', 'option_b', 'option_c', 'option_d']
      for (const k of keys) {
        if (!String(f[k] || '').trim()) {
          Message.warning('请填写四个选项文案（均不能为空）')
          return
        }
      }
      this.guessSaving = true
      try {
        const res = await saveDailyPuzzleGuessOptions(id, {
          option_a: f.option_a.trim(),
          option_b: f.option_b.trim(),
          option_c: f.option_c.trim(),
          option_d: f.option_d.trim(),
          correct: f.correct
        })
        if (res.success) {
          Message.success('已保存')
          this.guessDialogVisible = false
          await this.loadList()
        } else {
          Message.error(res.error || '保存失败')
        }
      } catch (e) {
        const d = e.response && e.response.data
        Message.error((d && d.error) || e.message || '保存失败')
      } finally {
        this.guessSaving = false
      }
    },
    async doUpload() {
      if (!this.pendingFiles.length) {
        Message.warning('请先选择图片')
        return
      }
      const fd = new FormData()
      this.pendingFiles.forEach((f) => fd.append('files', f))
      fd.append('difficulty', this.difficulty)
      this.uploading = true
      try {
        const res = await batchUploadDailyPuzzles(fd)
        if (res.success) {
          Message.success(`成功上传 ${res.count || 0} 条`)
          this.pendingFiles = []
          if (this.$refs.fileInput) this.$refs.fileInput.value = ''
          this.page = 1
          this.loadList()
        } else {
          Message.error(res.error || '上传失败')
        }
      } catch (e) {
        const msg =
          (e.response && e.response.data && e.response.data.error) || e.message || '上传失败'
        Message.error(msg)
      } finally {
        this.uploading = false
      }
    },
    onDelete(row) {
      MessageBox.confirm(`将逻辑删除并清理 MinIO。num=${row.num}，ID：${row.id}`, '确认删除', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning',
        confirmButtonClass: 'el-button--danger'
      })
        .then(async () => {
          this.deletingId = row.id
          try {
            const res = await deleteDailyPuzzle(row.id)
            if (res.success) {
              Message.success('已删除')
              this.loadList()
            } else {
              Message.error(res.error || '删除失败')
            }
          } catch (e) {
            const msg =
              (e.response && e.response.data && e.response.data.error) || e.message || '删除失败'
            Message.error(msg)
          } finally {
            this.deletingId = ''
          }
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.page-card {
  border-radius: 12px;
  border: none;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.07);
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.page-icon {
  font-size: 20px;
  line-height: 1;
}
.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* 排序提示 */
.sort-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 16px;
  padding: 10px 14px;
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
  background: #f4f6fb;
  border-radius: 8px;
  border-left: 3px solid #667eea;
}
.hint-icon {
  color: #667eea;
  font-size: 14px;
  flex-shrink: 0;
}

/* 上传栏 */
.upload-bar {
  margin-bottom: 4px;
  padding: 16px 16px 4px;
  background: #fafbff;
  border-radius: 10px;
  border: 1px solid #eef0f8;
}
.file-input-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}
.file-input {
  max-width: 260px;
  font-size: 13px;
}
.file-hint {
  font-size: 13px;
  color: #667eea;
  font-weight: 500;
}

/* 分页 */
.pager {
  margin-top: 20px;
  text-align: right;
}

/* 缩略图 */
.thumb-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
}
.thumb-img {
  width: 60px;
  height: 60px;
  border-radius: 6px;
  cursor: zoom-in;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);
}
.thumb-img--silhouette {
  background: #1e1e2e;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.18);
}
.thumb-fallback {
  width: 60px;
  height: 60px;
  line-height: 60px;
  font-size: 12px;
  color: #909399;
  background: #f5f7fa;
  border-radius: 6px;
  text-align: center;
  cursor: pointer;
  border: 1px dashed #d9dde4;
  transition: border-color 0.2s, color 0.2s;
}
.thumb-fallback:hover {
  border-color: #667eea;
  color: #667eea;
}
.thumb-fallback--muted {
  cursor: default;
  font-size: 11px;
  color: #c0c4cc;
  border-style: solid;
  border-color: #ebeef5;
}
.thumb-fallback--muted:hover {
  border-color: #ebeef5;
  color: #c0c4cc;
}

/* 每日一关弹窗 */
.tiles-dialog-body {
  min-height: 120px;
}
.tiles-meta {
  margin: 0 0 16px;
  padding: 10px 14px;
  font-size: 13px;
  color: #606266;
  word-break: break-all;
  background: #f4f6fb;
  border-radius: 8px;
}
.tiles-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 55vh;
  overflow-y: auto;
  padding: 4px 0;
}
.tile-cell {
  width: 76px;
  height: 76px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1);
}

/* 拖拽手柄 */
.row-drag-handle {
  cursor: grab;
  color: #c0c4cc;
  font-size: 18px;
  transition: color 0.2s;
}
.row-drag-handle:hover {
  color: #667eea;
}
.row-drag-handle:active {
  cursor: grabbing;
}
</style>

<style>
.col-drag .cell {
  overflow: visible;
}

.guess-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.guess-form {
  padding-right: 8px;
}
</style>
